import { LayoutDashboard, LineChart } from '@tamagui/lucide-icons';
import { keepPreviousData } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Input } from '@unistocks/components/input/input';
import { Column } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Paginator } from '@unistocks/components/paginator/paginator';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { getLocation, Table } from '@unistocks/components/table/table';
import { mapSortingToParam } from '@unistocks/components/table/utils';
import { AssetFilterSheet } from '@unistocks/features/admin/components/asset-management/asset-filter-sheet';
import { AssetListHeader } from '@unistocks/features/admin/components/asset-management/asset-list-header';
import { AssetListRow } from '@unistocks/features/admin/components/asset-management/asset-list-row';
import { AdminActions, selectAssetFilter } from '@unistocks/features/admin/store/admin.slice';
import {
  assetsGetListQueryKey,
  AssetsGetListAssetsQueryParameters as AssetsGetListQueryParameters,
  useAssetsGetListQuery
} from '@unistocks/proxy/axios-client/AssetsQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { useAppDispatch, useAppSelector } from '@unistocks/shared/hooks/redux-hooks';
import { t } from '@unistocks/shared/localization/index.ts';

export const AssetList: React.FC = () => {
  const dispatch = useAppDispatch();
  const assetFilter = useAppSelector(selectAssetFilter);
  const { filter, assetType, dataSource, maxResultCount, page, sorting } = assetFilter;

  const queryParams: AssetsGetListQueryParameters = {
    filter: filter ?? undefined,
    type: assetType ?? undefined,
    dataSource: dataSource ?? undefined,
    sorting: mapSortingToParam(sorting),
    skipCount: (page - 1) * maxResultCount,
    maxResultCount
  };

  const { data, isSuccess, isPending, isRefetching, refetch } = useAssetsGetListQuery(queryParams, {
    placeholderData: keepPreviousData
  });

  const onFilterChange = (newFilter: string) => {
    dispatch(
      AdminActions.setAssetFilter({
        ...assetFilter,
        page: 1,
        filter: newFilter
      })
    );
  };

  const onPageChange = (page: number) =>
    dispatch(AdminActions.setAssetFilter({ ...assetFilter, page }));

  return (
    <Page>
      <Page.ScrollView
        refreshing={isRefetching}
        onRefresh={refetch}
        headerTitle={t('::Menu:Assets')}
        headerIcon={<AssetFilterSheet />}
        breadcrumbs={[
          {
            key: 'admin',
            href: '/(admin)',
            name: t('::Menu:Administration'),
            rootPathName: 'admin',
            Icon: props => <LayoutDashboard {...props} />
          }
        ]}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Column gap={'$3'} mt={'$3'}>
          <Input
            placeholder={t('::Search')}
            value={filter}
            onChangeText={onFilterChange}
            $md={{
              size: '$3'
            }}
          />
          <Table
            transition="quickLessBouncy"
            animateOnly={['opacity', 'transform']}
            enterStyle={{ o: 0, y: -20 }}
          >
            <AssetListHeader isLoading={isPending} />

            <QueryState
              queryKeys={[assetsGetListQueryKey(queryParams)]}
              loadingText={t('::Assets:Loading')}
              isEmpty={isSuccess && !data.items.length}
              noDataIcon={LineChart}
              noDataText={t('::NoDataAvailableInDatatable')}
            >
              <Table.Body key={'table-body'} {...asyncStateTransition}>
                {data?.items.map((a, i, arr) => (
                  <AssetListRow key={a.id} asset={a} rowLocation={getLocation(i, arr)} />
                ))}
              </Table.Body>

              <Table.Foot key={'table-foot'} {...asyncStateTransition}>
                <Table.Row>
                  <Table.Cell alignCells={{ x: 'center' }}>
                    <Paginator
                      flex={1}
                      itemsPerPage={MAX_RESULT_COUNT}
                      currentPage={page}
                      totalItems={data?.totalCount ?? 0}
                      onPageChange={onPageChange}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Foot>
            </QueryState>
          </Table>
        </Column>
      </Page.ScrollView>
    </Page>
  );
};

AssetList.displayName = 'AssetList';
